import React from 'react';
import { X, Check, Sparkles, Shield, Zap } from 'lucide-react';
import { usePERentStore, PERentLicenseType } from '../../store/usePERentStore';

export interface PERentPaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const COACH_HINT =
  'Wichtig: Monatsabo und Jahreskauf beinhalten nicht nur den Tool-Zugang, sondern auch die aktive Coach-Betreuung bei PERent. Viele Schritte lassen sich sinnvoll nur mit Begleitung umsetzen — die Unterstützung deines Coaches ist im Preis enthalten.';

export const PERentPaywallModal: React.FC<PERentPaywallModalProps> = ({ isOpen, onClose }) => {
  const { setLicense } = usePERentStore();

  if (!isOpen) return null;

  const handleSelectPlan = (type: PERentLicenseType) => {
    if (!type) return;
    setLicense(type);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-900 p-6 text-slate-50 shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-800 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-50">PERent Premium freischalten</h2>
              <p className="text-xs text-slate-400">Stuhlmiete-Kalkulation inkl. Coach-Betreuung</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-center">
            <Shield className="w-4 h-4 text-amber-400 mx-auto mb-1.5" />
            <span className="text-xs font-semibold text-slate-200 block">Echte Salondaten</span>
            <span className="text-[11px] text-slate-400">Direkte BWA-Kostenbasis</span>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-center">
            <Zap className="w-4 h-4 text-amber-400 mx-auto mb-1.5" />
            <span className="text-xs font-semibold text-slate-200 block">Salami-Modus</span>
            <span className="text-[11px] text-slate-400">Woche, Monat & Override</span>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-center">
            <Sparkles className="w-4 h-4 text-amber-400 mx-auto mb-1.5" />
            <span className="text-xs font-semibold text-slate-200 block">Coach inklusive</span>
            <span className="text-[11px] text-slate-400">Betreuung im Preis</span>
          </div>
        </div>

        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
          <p className="text-sm leading-relaxed text-slate-200">{COACH_HINT}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative flex flex-col justify-between rounded-xl border-2 border-amber-500/80 bg-slate-800/90 p-5 shadow-lg shadow-amber-500/10">
            <div className="absolute -top-3 right-3 rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-extrabold text-slate-950 tracking-wide leading-tight text-center">
              Empfohlen &amp; Preisvorteil
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-400 block mb-1">
                Monatsabo
              </span>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-extrabold text-slate-50">14,99 €</span>
                <span className="text-xs text-slate-400">/ Monat</span>
              </div>
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                Preisvorteil: günstiger Einstieg. Abo verlängert sich ab Freischaltung automatisch;
                nach 12 Monaten monatlich kündbar. Inkl. Coach-Betreuung.
              </p>
              <ul className="space-y-2 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Alle Updates & Erweiterungen</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Coach-Betreuung inklusive</span>
                </li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => handleSelectPlan('sub_24')}
              className="mt-6 w-full rounded-lg bg-amber-500 hover:bg-amber-400 px-4 py-2.5 text-xs font-extrabold text-slate-950 transition-all shadow-md"
            >
              Abo aktivieren
            </button>
          </div>

          <div className="relative flex flex-col justify-between rounded-xl border border-slate-700 bg-slate-800/60 p-5 hover:border-slate-600 transition-all">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-1">
                Einmalzahlung
              </span>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-extrabold text-slate-50">199 €</span>
                <span className="text-xs text-slate-400">/ Jahr</span>
              </div>
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                Voller Zugriff für 12 Monate in einer Zahlung. Keine automatische Verlängerung.
                Inkl. Coach-Betreuung.
              </p>
              <ul className="space-y-2 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Unbegrenzte Berechnungen</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Coach-Betreuung inklusive</span>
                </li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => handleSelectPlan('single_300')}
              className="mt-6 w-full rounded-lg bg-slate-700 hover:bg-slate-600 px-4 py-2.5 text-xs font-bold text-slate-100 transition-all shadow"
            >
              Einmalzahlung wählen
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-500">
          Alle Preise zzgl. gesetzlicher MwSt. Rechnungsstellung erfolgt über PreisExpert.
        </p>
      </div>
    </div>
  );
};
